import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { FadeUp, CountUp } from "@/components/Motion";
import { Sparkles, Layers, Building2, ArrowRight } from "lucide-react";

const CLIENTS = ["Travel + Leisure", "Mitsubishi Power", "DDEC Puerto Rico", "ODP", "Amazon", "Disney", "NMSDC"];

export default function About() {
  return (
    <Layout>
      <section className="container-summit py-16 md:py-24 max-w-3xl">
        <FadeUp className="overline mb-6"><span className="accent-rule" />Produced By</FadeUp>
        <FadeUp delay={0.05}>
          <h1 className="serif text-5xl sm:text-7xl">Display America</h1>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="mt-8 text-lg text-zinc-600 leading-relaxed">
            Display America is the production studio behind the Travel + Leisure Strategic Supplier Summit.
            We design and operate executive-grade brand experiences for the world's most discerning enterprise teams,
            from exhibit fabrication to end-to-end event programming.
          </p>
        </FadeUp>
        <div className="mt-12 grid sm:grid-cols-3 gap-px bg-[hsl(var(--line))]">
          <Stat n="20+" l="Years of Production" />
          <Stat n="500+" l="Executive Events" />
          <Stat n="1:1" l="White-glove Service" />
        </div>
      </section>

      {/* Platform Section */}
      <section className="border-t border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-16 md:py-24 grid lg:grid-cols-[1.2fr_1fr] gap-12">
          <FadeUp>
            <div className="overline text-zinc-400 mb-6"><span className="accent-rule" />The Platform</div>
            <h2 className="serif text-4xl sm:text-5xl">A digital experience, custom-built.</h2>
            <p className="mt-6 text-zinc-300 leading-relaxed max-w-xl">
              The platform behind this summit was designed and built by Display America for Travel <span className="text-[#A39171]">+</span> Leisure.
              It powers the digital experience before, during, and after the event — registration, AI concierge,
              executive dashboards, lead capture, matchmaking, and operations — as a single, executive-grade portal.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/demo" className="btn-black bg-white text-black hover:bg-zinc-200">
                See it live <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-outline-black border-white text-white hover:bg-white hover:text-black">
                Talk to us
              </Link>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="overline text-zinc-400 mb-4">Designed for</div>
            <div className="grid grid-cols-2 gap-3">
              {CLIENTS.map((c) => (
                <div key={c} className="border border-white/10 p-4">
                  <Building2 size={14} className="text-[#A39171] mb-2" strokeWidth={1.4} />
                  <div className="serif text-base">{c}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/10 mt-6">
              <div className="bg-[#0A0A0A] p-5">
                <div className="serif text-3xl"><CountUp value={11} /></div>
                <div className="overline mt-1">Modules</div>
              </div>
              <div className="bg-[#0A0A0A] p-5">
                <div className="serif text-3xl"><CountUp value={1} />×</div>
                <div className="overline mt-1">Clone to launch</div>
              </div>
              <div className="bg-[#0A0A0A] p-5">
                <div className="serif text-3xl">∞</div>
                <div className="overline mt-1">Events</div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Modules */}
      <section className="container-summit py-16">
        <FadeUp className="overline mb-6"><span className="accent-rule" />What's inside the platform</FadeUp>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[hsl(var(--line))]">
          {[
            ["Magic-link Auth", "Passwordless executive access"],
            ["Exhibitor Registration", "Stripe-backed packages"],
            ["AI Concierge", "Trained on your event"],
            ["Matchmaking", "Business Match Scores"],
            ["Executive Dashboard", "CEO-grade KPIs"],
            ["Operations Center", "Day-of ticketing board"],
            ["Lead Capture", "5-star ratings + CSV"],
            ["Resource Library", "Searchable repository"],
            ["Comms Schedule", "T-30 → renewal"],
            ["Interactive Venue", "Floor plan + favorites"],
            ["Social Toolkit", "Personalized graphics"],
            ["White-label Clone", "New event in minutes"],
          ].map(([t, s]) => (
            <FadeUp key={t} delay={0.02}>
              <div className="bg-white p-6 h-full">
                <Layers size={16} className="text-[#A39171]" strokeWidth={1.4} />
                <div className="serif text-lg mt-3">{t}</div>
                <div className="overline mt-1">{s}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
    </Layout>
  );
}

function Stat({ n, l }) {
  return (
    <FadeUp>
      <div className="bg-white p-8 text-center h-full">
        <div className="serif text-3xl">{n}</div>
        <div className="overline mt-2">{l}</div>
      </div>
    </FadeUp>
  );
}
